import React, { useState, useRef, useEffect } from 'react';
import {
    ChevronRight, ChevronLeft, Plus, X, Check, Upload, Globe,
    GraduationCap, MessageCircle, Phone, MapPin, Video, Camera,
    Play, Sparkles, Loader2, Lightbulb, RefreshCw, Map, Navigation,
    Globe2, Calendar
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { get, isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-number-input';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import ReactPlayer from 'react-player';

// Project specific imports
import { getTeacherOnboardData } from "../../../../store/actions/teacherOnboard/teacherOnboardAction";
import { LANGUAGES } from "../../../../utils/constants";
import "react-phone-number-input/style.css";

const apiKey = process.env.REACT_APP_GEMINI_API_KEY || "";

const Onboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const onBoardData = useSelector((state) => get(state, "teacherOnboardReducer.teacherOnboardData", {}));

    const [step, setStep] = useState(1);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const totalSteps = 10;
    const fileInputRef = useRef(null);

    const availableYears = Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i + 5);

    // --- FORM STATE ---
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        gender: '',
        birthDate: '2000-01-01',
        mobile: '',
        teacherType: '',
        originCountry: '',
        originState: '',
        livingCountry: '',
        livingState: '',
        profileImage: null,
        profileImageFile: null,
        motherTongue: '',
        spokenLanguages: [],
        teachingLanguages: [],
        education: { title: '', institute: '', location: '', startYear: '', endYear: '', desc: '' },
        videoUrl: '',
        selfIntro: ''
    });

    const hasPopulated = useRef(false);
    useEffect(() => {
        let profile = JSON.parse(window.localStorage.getItem("profile") || "{}");
        if (profile.isOnBoarding) navigate("/teacher/dashboard");

        if (!isEmpty(onBoardData) && !hasPopulated.current) {
            setFormData(prev => ({
                ...prev,
                firstName: get(onBoardData, "firstName", prev.firstName),
                lastName: get(onBoardData, "lastName", prev.lastName),
                gender: get(onBoardData, "gender", prev.gender),
                teacherType: get(onBoardData, "teacherType", prev.teacherType),
                birthDate: onBoardData.dob ? new Date(onBoardData.dob).toISOString().split('T')[0] : prev.birthDate,
                motherTongue: get(onBoardData, "motherTongue", prev.motherTongue),
                originCountry: get(onBoardData, "region.fromCountry", prev.originCountry),
                originState: get(onBoardData, "region.fromState", prev.originState),
                livingCountry: get(onBoardData, "region.currentCountry", prev.livingCountry),
                livingState: get(onBoardData, "region.currentState", prev.livingState),
                mobile: get(onBoardData, "mobileNumber", prev.mobile),
                videoUrl: get(onBoardData, "videoURL", prev.videoUrl),
                selfIntro: get(onBoardData, "selfIntro", prev.selfIntro),
            }));
            hasPopulated.current = true;
        }
    }, [onBoardData, navigate]);

    const [aiSuggestions, setAiSuggestions] = useState({ photoTips: [], niches: [] });

    const updateForm = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));
    const updateEducation = (key, value) => setFormData(prev => ({
        ...prev,
        education: { ...prev.education, [key]: value }
    }));

    // --- VALIDATION LOGIC ---
    const validateStep = (currentStep) => {
        const isAlpha = (ch) => /^[a-zA-Z\s]*$/.test(ch);
        const calculateAge = (birthday) => {
            const ageDifMs = Date.now() - new Date(birthday).getTime();
            const ageDate = new Date(ageDifMs);
            return Math.abs(ageDate.getUTCFullYear() - 1970);
        };

        switch (currentStep) {
            case 1:
                if (!formData.firstName.trim() || !formData.lastName.trim()) return "First and Last names are required";
                if (!isAlpha(formData.firstName) || !isAlpha(formData.lastName)) return "Names should only contain letters";
                break;
            case 2:
                if (!formData.gender) return "Please select a gender";
                break;
            case 3:
                if (!formData.birthDate) return "Please choose your date of birth";
                if (calculateAge(formData.birthDate) < 18) return "You must be 18 years or older to register";
                break;
            case 4:
                if (!formData.mobile || formData.mobile.length < 10) return "Please enter a valid mobile number";
                break;
            case 5:
                if (!formData.teacherType) return "Please select your teacher type";
                break;
            case 6:
                if (!formData.originCountry || !formData.originState || !formData.livingCountry || !formData.livingState) return "Please fill all location fields";
                break;
            case 7:
                if (!formData.profileImageFile && !onBoardData.teacherProfilePic) return "Please upload a profile picture";
                break;
            case 8:
                if (!formData.motherTongue.trim()) return "Mother tongue is required";
                if (formData.spokenLanguages.length === 0) return "Please add at least one spoken language";
                if (formData.teachingLanguages.length === 0) return "Please add at least one teaching language";
                break;
            case 9:
                if (!formData.education.title.trim() || !formData.education.institute.trim() || !formData.education.startYear || !formData.education.endYear) return "Complete education details are required";
                if (parseInt(formData.education.startYear) > parseInt(formData.education.endYear)) return "Start Year must be before or equal to End Year";
                break;
            case 10:
                const ytRegex = /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
                let vimeoRegex = /(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_-]+)?/i;
                if (!formData.videoUrl || (!formData.videoUrl.match(ytRegex) && !formData.videoUrl.match(vimeoRegex))) return "Please share a valid YouTube or Vimeo URL";
                break;
            default: return null;
        }
        return null;
    }

    const nextStep = () => {
        const errorMsg = validateStep(step);
        if (errorMsg) {
            toast.error(errorMsg);
            setError(errorMsg);
            return;
        }
        setError(null);
        if (step === totalSteps) {
            handleSubmit();
        } else {
            setStep(prev => Math.min(prev + 1, totalSteps));
        }
    };
    const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

    // --- GEMINI API CALLER ---
    const callGemini = async (prompt, systemInstruction = "You are a helpful education assistant.") => {
        if (!apiKey) {
            console.warn("Gemini API key missing");
            return null;
        }
        setIsGenerating(true);
        const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=${apiKey}`;
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contents: [{ parts: [{ text: `${systemInstruction}\n\n${prompt}` }] }]
                })
            });
            const data = await response.json();
            setIsGenerating(false);
            return data.candidates?.[0]?.content?.parts?.[0]?.text;
        } catch (err) {
            setIsGenerating(false);
            return null;
        }
    };

    const getPhotoTips = async () => {
        const res = await callGemini("List 4 brief, professional tips for a teacher's profile picture. Focus on background, lighting, and attire. Keep each tip under 10 words.");
        if (res) {
            const tips = res.split('\n')
                .map(t => t.replace(/^\d+\.\s*/, '').replace(/^[-*]\s*/, '').trim())
                .filter(t => t.length > 5)
                .slice(0, 4);
            setAiSuggestions(prev => ({ ...prev, photoTips: tips }));
        }
    };

    const getNiches = async () => {
        const res = await callGemini(`Suggest 4 specialized teaching niches (sub-topics) for a ${formData.motherTongue} teacher living in ${formData.livingCountry}.`, "Return ONLY a comma-separated list of 4 short niches.");
        if (res) setAiSuggestions(prev => ({ ...prev, niches: res.split(',').map(s => s.trim()) }));
    };

    // --- SUBMIT LOGIC ---
    const handleSubmit = async () => {
        setIsLoading(true);
        const data = new FormData();
        data.append("firstName", formData.firstName);
        data.append("lastName", formData.lastName);
        data.append("gender", formData.gender);
        data.append("teacherType", formData.teacherType);
        data.append("dob", new Date(formData.birthDate));
        data.append("languageSpeak", JSON.stringify(formData.spokenLanguages));
        data.append("languageTeach", JSON.stringify(formData.teachingLanguages));
        data.append("fromCountry", formData.originCountry);
        data.append("currentCountry", formData.livingCountry);
        data.append("fromState", formData.originState);
        data.append("currentState", formData.livingState);
        data.append("mobileNumber", formData.mobile);
        data.append("videoURL", formData.videoUrl);
        data.append("selfIntro", formData.selfIntro || "Hello students!");
        data.append("motherTongue", formData.motherTongue);
        if (formData.profileImageFile) data.append("teacherProfilePic", formData.profileImageFile);

        const eduValues = {
            title: formData.education.title,
            institute: formData.education.institute,
            locations: formData.originCountry,
            from: formData.education.startYear,
            to: formData.education.endYear,
            description: formData.education.desc || ""
        };
        data.append("educationDetails", JSON.stringify([eduValues]));
        data.append("workExperience", JSON.stringify([]));
        data.append("certificateCourses", JSON.stringify([]));

        const result = await dispatch(getTeacherOnboardData(data));
        setIsLoading(false);

        if (result && (result.msg === "Teacher Onboard process completed." || result.msg === "Teacher Onboard process already completed.")) {
            toast.success("Onboarding Completed");
            let profile = JSON.parse(window.localStorage.getItem("profile") || "{}");
            profile.isOnBoarding = true;
            window.localStorage.setItem("profile", JSON.stringify(profile));
            navigate("/teacher/dashboard");
        } else {
            const firstError = result?.errors?.[0]?.msg || result?.errors?.[0]?.message;
            toast.error(firstError || result?.msg || result?.message || "Failed to OnBoard");
        }
    };

    const StepHeader = ({ title, sub }) => (
        <div className="text-center mb-10 animate-in fade-in slide-in-from-top-4 duration-500">
            <h1 className="text-4xl font-black text-slate-900 mb-3 tracking-tight">{title}</h1>
            {sub && <p className="text-slate-500 text-lg max-w-lg mx-auto">{sub}</p>}
        </div>
    );

    const NavButtons = () => (
        <div className="w-full max-w-2xl flex items-center justify-between mt-12 pt-8 border-t border-slate-100">
            {step > 1 ? (
                <button onClick={prevStep} className="flex items-center gap-2 px-8 py-3 font-bold text-slate-400 hover:text-rose-500 transition-colors">
                    <ChevronLeft size={20} /> Previous
                </button>
            ) : <div />}
            <button onClick={nextStep} disabled={isLoading} className="group flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-rose-500 to-pink-500 text-white font-black rounded-2xl shadow-xl shadow-rose-100 hover:shadow-rose-200 hover:-translate-y-1 transition-all active:scale-95">
                {isLoading ? <Loader2 size={22} className="animate-spin" /> : step === totalSteps ? 'Finish Profile' : 'Next Step'} <ChevronRight size={22} className="group-hover:translate-x-1 transition-transform" />
            </button>
        </div>
    );

    const renderStepContent = () => {
        switch (step) {
            case 1: return (
                <div className="w-full max-w-2xl flex flex-col items-center">
                    <StepHeader title="What is your name?" sub="Let's start with the basics for your teacher profile." />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                        <input type="text" placeholder="First Name" className="w-full p-5 bg-white border-2 border-slate-50 rounded-2xl shadow-sm outline-none focus:border-rose-300 focus:ring-4 focus:ring-rose-50 transition-all font-bold text-slate-700" value={formData.firstName} onChange={e => updateForm('firstName', e.target.value)} />
                        <input type="text" placeholder="Last Name" className="w-full p-5 bg-white border-2 border-slate-50 rounded-2xl shadow-sm outline-none focus:border-rose-300 focus:ring-4 focus:ring-rose-50 transition-all font-bold text-slate-700" value={formData.lastName} onChange={e => updateForm('lastName', e.target.value)} />
                    </div>
                </div>
            );
            case 2: return (
                <div className="w-full max-w-3xl flex flex-col items-center">
                    <StepHeader title="What is your gender?" sub="This helps us customize your experience." />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
                        {['Male', 'Female', 'Others'].map(g => (
                            <button key={g} onClick={() => updateForm('gender', g)} className={`p-8 rounded-[40px] border-4 transition-all flex flex-col items-center gap-6 ${formData.gender === g ? 'border-rose-400 bg-rose-50/50 shadow-xl shadow-rose-100 scale-105' : 'border-white bg-white shadow-sm hover:border-slate-50'}`}>
                                <div className={`w-24 h-24 rounded-full flex items-center justify-center text-4xl shadow-inner ${g === 'Male' ? 'bg-blue-50' : g === 'Female' ? 'bg-pink-50' : 'bg-slate-50'}`}>
                                    {g === 'Male' ? '👦' : g === 'Female' ? '👧' : '👤'}
                                </div>
                                <span className="font-black text-slate-700 text-lg">{g}</span>
                            </button>
                        ))}
                    </div>
                </div>
            );
            case 3: return (
                <div className="w-full max-w-2xl flex flex-col items-center">
                    <StepHeader title="When were you born?" sub="Select your date of birth below." />
                    <div className="bg-white p-10 rounded-[48px] shadow-2xl shadow-slate-200/50 border-4 border-white flex flex-col items-center">
                        <div className="bg-rose-50 p-4 rounded-3xl text-rose-500 mb-6"><Calendar size={48} /></div>
                        <input type="date" className="text-3xl font-black text-slate-700 outline-none cursor-pointer hover:text-rose-500 transition-colors" value={formData.birthDate} onChange={e => updateForm('birthDate', e.target.value)} />
                    </div>
                </div>
            );
            case 4: return (
                <div className="w-full max-w-2xl flex flex-col items-center">
                    <StepHeader title="Your mobile number" sub="We use this for secure account verification." />
                    <div className="flex gap-4 w-full p-4 bg-white rounded-3xl border-4 border-white shadow-xl">
                        <div className="flex items-center gap-2 pl-4 border-r-2 border-slate-50 pr-6 text-slate-700 font-black text-xl">
                            <Phone size={24} className="text-rose-400" /> +91
                        </div>
                        <PhoneInput placeholder="98765 43210" value={formData.mobile} onChange={val => updateForm('mobile', val)} className="flex-1 p-4 outline-none font-black text-2xl text-slate-700" />
                    </div>
                </div>
            );
            case 5: return (
                <div className="w-full max-w-4xl flex flex-col items-center">
                    <StepHeader title="What describes you best?" sub="Professional expert or community and culture guide?" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 w-full max-w-3xl">
                        {['Professional Teacher', 'Community Teacher'].map(type => (
                            <button key={type} onClick={() => updateForm('teacherType', type)} className={`p-10 rounded-[56px] border-4 transition-all text-center group ${formData.teacherType === type ? 'border-rose-400 bg-rose-50/20 scale-105 shadow-2xl' : 'border-white bg-white shadow-xl hover:shadow-2xl'}`}>
                                <div className="w-40 h-52 bg-slate-50 rounded-3xl mx-auto mb-8 flex items-center justify-center overflow-hidden">
                                    <div className={`w-full h-full bg-gradient-to-br ${type === 'Professional Teacher' ? 'from-indigo-100 to-indigo-300 opacity-60' : 'from-rose-100 to-rose-300 opacity-60'}`}></div>
                                </div>
                                <h3 className="text-2xl font-black text-slate-800">{type}</h3>
                            </button>
                        ))}
                    </div>
                </div>
            );
            case 6: return (
                <div className="w-full max-w-4xl flex flex-col items-center">
                    <StepHeader title="Where are you from?" sub="Country and region details help students find you." />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
                        <div className="bg-white p-8 rounded-[40px] shadow-lg border relative overflow-hidden">
                            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2"><MapPin className="text-rose-400" size={18} /> Origin</h3>
                            <div className="space-y-4">
                                <CountryDropdown classes="w-full p-4 bg-slate-50 rounded-2xl font-bold appearance-none outline-none" value={formData.originCountry} onChange={val => updateForm('originCountry', val)} />
                                <RegionDropdown classes="w-full p-4 bg-slate-50 rounded-2xl font-bold appearance-none outline-none" country={formData.originCountry} value={formData.originState} onChange={val => updateForm('originState', val)} />
                            </div>
                        </div>
                        <div className="bg-white p-8 rounded-[40px] shadow-lg border relative overflow-hidden">
                            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2"><Navigation className="text-indigo-400" size={18} /> Living In</h3>
                            <div className="space-y-4">
                                <CountryDropdown classes="w-full p-4 bg-slate-50 rounded-2xl font-bold appearance-none outline-none" value={formData.livingCountry} onChange={val => updateForm('livingCountry', val)} />
                                <RegionDropdown classes="w-full p-4 bg-slate-50 rounded-2xl font-bold appearance-none outline-none" country={formData.livingCountry} value={formData.livingState} onChange={val => updateForm('livingState', val)} />
                            </div>
                        </div>
                    </div>
                </div>
            );
            case 7: return (
                <div className="w-full max-w-4xl flex flex-col items-center">
                    <StepHeader title="Upload profile picture" sub="A professional photo built on trust and clarity." />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 w-full items-center">
                        <div onClick={() => fileInputRef.current?.click()} className="w-72 h-72 rounded-full border-4 border-dashed border-rose-100 flex items-center justify-center cursor-pointer hover:border-rose-400 hover:bg-rose-50 transition-all bg-white shadow-2xl relative overflow-hidden group mx-auto">
                            {formData.profileImage ? <img src={formData.profileImage} alt="Profile" className="w-full h-full object-cover" /> : <div className="text-center group-hover:scale-110 transition-transform"><Camera size={48} className="text-rose-400 mx-auto mb-4" /><p className="font-black text-slate-800">Upload Photo</p></div>}
                            <input ref={fileInputRef} type="file" className="hidden" accept="image/*" onChange={e => {
                                const f = e.target.files[0];
                                if (f) { updateForm('profileImageFile', f); updateForm('profileImage', URL.createObjectURL(f)); }
                            }} />
                        </div>
                        <div className="bg-slate-900 rounded-[48px] p-8 text-white relative">
                            <div className="flex items-center justify-between mb-8">
                                <h3 className="text-xl font-black flex items-center gap-3"><Lightbulb className="text-yellow-400" /> AI Photo Coach</h3>
                                <button onClick={getPhotoTips} disabled={isGenerating} className="p-2 bg-white/10 rounded-xl hover:bg-white/20"><RefreshCw size={20} className={isGenerating ? 'animate-spin' : ''} /></button>
                            </div>
                            <div className="space-y-4 min-h-[150px]">
                                {aiSuggestions.photoTips.length > 0 ? aiSuggestions.photoTips.map((tip, i) => (
                                    <div key={i} className="flex gap-4 items-start"><p className="text-sm font-medium">{tip}</p></div>
                                )) : <div className="text-slate-500 italic text-sm text-center">Click refresh for AI photo tips!</div>}
                            </div>
                        </div>
                    </div>
                </div>
            );
            case 8: return (
                <div className="w-full max-w-4xl flex flex-col items-center">
                    <StepHeader title="Language Skills" sub="Select languages from the dropdown list." />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                        <div className="md:col-span-2 bg-white p-6 rounded-[30px] border">
                            <label className="text-xs font-black text-slate-400 uppercase tracking-widest block mb-2">Primary Mother Tongue</label>
                            <select className="w-full p-4 bg-slate-50 rounded-2xl font-black text-xl outline-none appearance-none" value={formData.motherTongue} onChange={e => updateForm('motherTongue', e.target.value)}>
                                <option value="">Select Language</option>
                                {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                            </select>
                        </div>
                        <div className="bg-white p-6 rounded-[30px] border">
                            <h3 className="font-black text-lg mb-4 flex items-center gap-3"><Globe size={22} className="text-rose-400" /> Spoken</h3>
                            <div className="flex gap-2">
                                <select id="spoken-in" className="flex-1 p-3 bg-slate-50 rounded-xl font-bold outline-none appearance-none">
                                    <option value="">Choose...</option>
                                    {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                                </select>
                                <button onClick={() => {
                                    const val = document.getElementById('spoken-in').value;
                                    if (val && !formData.spokenLanguages.find(l => l.lang === val)) { updateForm('spokenLanguages', [...formData.spokenLanguages, { lang: val, level: 'Fluent' }]); }
                                }} className="bg-rose-500 text-white p-3 rounded-xl"><Plus size={20} /></button>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {formData.spokenLanguages.map((l, i) => <span key={i} className="px-4 py-2 bg-slate-100 rounded-full font-bold text-sm flex items-center gap-2">{l.lang} <X size={14} className="cursor-pointer" onClick={() => updateForm('spokenLanguages', formData.spokenLanguages.filter((_, idx) => idx !== i))} /></span>)}
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-[30px] border">
                            <h3 className="font-black text-lg mb-4 flex items-center gap-3"><GraduationCap size={22} className="text-indigo-400" /> Teaching</h3>
                            <div className="flex gap-2">
                                <select id="teach-in" className="flex-1 p-3 bg-slate-50 rounded-xl font-bold outline-none appearance-none">
                                    <option value="">Choose...</option>
                                    {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                                </select>
                                <button onClick={() => {
                                    const val = document.getElementById('teach-in').value;
                                    if (val && !formData.teachingLanguages.find(l => l.lang === val)) { updateForm('teachingLanguages', [...formData.teachingLanguages, { lang: val, subjects: ['General'] }]); }
                                }} className="bg-indigo-500 text-white p-3 rounded-xl"><Plus size={20} /></button>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">{formData.teachingLanguages.map((l, i) => <span key={i} className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full font-bold text-sm flex items-center gap-2">{l.lang} <X size={14} className="cursor-pointer" onClick={() => updateForm('teachingLanguages', formData.teachingLanguages.filter((_, idx) => idx !== i))} /></span>)}</div>
                            <button onClick={getNiches} className="mt-4 w-full py-2 bg-indigo-50 text-indigo-600 rounded-xl font-bold text-xs uppercase tracking-widest">+ Suggestions</button>
                        </div>
                    </div>
                </div>
            );
            case 9: return (
                <div className="w-full max-w-4xl flex flex-col items-center">
                    <StepHeader title="Education Details" sub="Ensure the end year is after the start year." />
                    <div className="bg-white p-8 rounded-[48px] border w-full max-w-3xl space-y-6 shadow-sm">
                        <input type="text" placeholder="Degree Title" className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none" value={formData.education.title} onChange={e => updateEducation('title', e.target.value)} />
                        <input type="text" placeholder="University / Institute" className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none" value={formData.education.institute} onChange={e => updateEducation('institute', e.target.value)} />
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-300 ml-2">Start Year</label>
                                <select className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none appearance-none" value={formData.education.startYear} onChange={e => updateEducation('startYear', e.target.value)}><option value="">Select</option>{availableYears.map(y => <option key={y} value={y}>{y}</option>)}</select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-300 ml-2">End Year</label>
                                <select className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none appearance-none" value={formData.education.endYear} onChange={e => updateEducation('endYear', e.target.value)}><option value="">Select</option>{availableYears.map(y => <option key={y} value={y}>{y}</option>)}</select>
                            </div>
                        </div>
                        <div className="p-10 border-4 border-dashed rounded-[40px] text-center bg-slate-50 border-slate-100"><Upload size={48} className="mx-auto text-slate-200 mb-4" /><p className="font-bold text-slate-300">Mandatory Certificate Upload</p></div>
                    </div>
                </div>
            );
            case 10: return (
                <div className="w-full max-w-3xl flex flex-col items-center"><StepHeader title="Introduction Video" sub="Students want to hear your accent! Paste a YouTube/Vimeo link." />
                    <div className="w-full space-y-8">
                        <div className="bg-white p-4 rounded-3xl border flex items-center gap-4 shadow-xl"><Play className="text-rose-500" fill="currentColor" size={24} /><input type="text" placeholder="Paste link here..." className="flex-1 outline-none font-bold text-slate-700 text-lg" value={formData.videoUrl} onChange={e => updateForm('videoUrl', e.target.value)} /></div>
                        <div className="aspect-video w-full bg-slate-900 rounded-[56px] shadow-2xl overflow-hidden relative">{formData.videoUrl && <ReactPlayer url={formData.videoUrl} width="100%" height="100%" />}{!formData.videoUrl && <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-700"><Video size={64} className="mb-4 opacity-10" /><p className="text-xs font-black uppercase opacity-20">Preview Area</p></div>}</div>
                    </div>
                </div>
            );
            default: return null;
        }
    };

    return (
        <div className="min-h-screen bg-[#FDFDFD] flex flex-col items-center py-12 px-6 font-sans text-slate-800 relative overflow-hidden w-full">
            <div className="fixed -top-40 -left-40 w-[600px] h-[600px] bg-rose-50 rounded-full blur-[140px] opacity-40 -z-10"></div>
            <div className="fixed -bottom-40 -right-40 w-[600px] h-[600px] bg-indigo-50 rounded-full blur-[140px] opacity-40 -z-10"></div>
            <div className="w-full max-w-2xl mb-16 relative"><div className="flex items-center justify-between">{[...Array(totalSteps)].map((_, i) => (<div key={i} className="flex flex-col items-center flex-1 relative z-10"><div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-black transition-all ${i + 1 < step ? 'bg-emerald-500 text-white' : i + 1 === step ? 'bg-rose-500 text-white shadow-xl scale-125' : 'bg-white text-slate-200 border-2'}`}>{i + 1 < step ? <Check size={20} strokeWidth={4} /> : i + 1}</div><span className={`absolute -bottom-8 text-[9px] font-black uppercase tracking-widest ${i + 1 === step ? 'text-rose-500' : 'hidden'}`}>{['Name', 'Gender', 'Birth', 'Mobile', 'Type', 'Loc', 'Photo', 'Skills', 'Edu', 'Video'][i]}</span></div>))}<div className="absolute top-5 left-0 w-full h-[3px] bg-slate-50 -z-0"></div></div></div>
            <div className="flex-1 w-full flex flex-col items-center justify-center py-8">{renderStepContent()}<NavButtons /></div>
            {error && (<div className="fixed top-8 right-8 bg-white border-l-4 border-red-500 p-4 rounded-xl shadow-2xl z-50 animate-in slide-in-from-right-10 flex gap-4 items-center"><X size={20} className="text-red-500 cursor-pointer" onClick={() => setError(null)} /><span className="text-sm font-bold text-slate-700">{error}</span></div>)}
        </div>
    );
};

export default Onboard;
